import React, { useEffect, useRef, memo, useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Box, Typography, List, ListItem, Link, CircularProgress } from '@mui/material';
import mermaid from 'mermaid';

// Process Unicode escape sequences in content
export const processUnicodeContent = (content) => {
  if (!content) return '';
  
  // Process \uXXXX Unicode escape sequences (BMP characters)
  const bmpProcessed = content.replace(/\\u([0-9a-fA-F]{4})/g, (_, codePoint) => {
    return String.fromCharCode(parseInt(codePoint, 16));
  });
  
  // Process \uXXXXXXXX Unicode escape sequences (supplementary plane characters)
  return bmpProcessed.replace(/\\u([0-9a-fA-F]{8})/g, (_, codePoint) => {
    const highSurrogate = parseInt(codePoint.substring(0, 4), 16);
    const lowSurrogate = parseInt(codePoint.substring(4, 8), 16);
    return String.fromCharCode(highSurrogate, lowSurrogate);
  });
};

// Initialize mermaid once at module level
let mermaidInitialized = false;
const initializeMermaid = () => {
  if (!mermaidInitialized) {
    try {
      mermaid.initialize({
        startOnLoad: false,
        theme: 'default',
        securityLevel: 'loose',
        flowchart: {
          useMaxWidth: true,
          htmlLabels: true,
          curve: 'basis',
          nodeSpacing: 50,
          rankSpacing: 50,
          padding: 15
        },
        sequence: {
          useMaxWidth: true,
          diagramMarginX: 50,
          diagramMarginY: 10,
          noteMargin: 10,
          messageMargin: 35,
          boxTextMargin: 5
        },
        classDiagram: { useMaxWidth: true },
        er: { useMaxWidth: true, fontSize: 14 },
        gantt: { useMaxWidth: true, fontSize: 14 },
        themeVariables: {
          fontSize: '18px',
          fontFamily: 'Arial, sans-serif',
          primaryTextColor: '#333',
          lineColor: '#555',
          nodeBorder: '#666',
          mainBkg: '#f5f5f5',
          nodeTextColor: '#333'
        }
      });
      mermaidInitialized = true;
    } catch (err) {
      console.error('Error initializing mermaid:', err);
    }
  }
};

// Mermaid diagram component
const MermaidDiagram = ({ code, index, zoomLevel = 1 }) => {
  const diagramRef = useRef(null);
  const [isLoading, setIsLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const renderAttemptRef = useRef(0);

  useEffect(() => {
    // Initialize mermaid if not already done
    initializeMermaid();

    let isMounted = true;
    let timeoutId = null;
    const currentAttempt = ++renderAttemptRef.current;

    const renderDiagram = async () => {
      // Set a timeout to prevent infinite loading (10 seconds max)
      timeoutId = setTimeout(() => {
        if (isMounted) {
          console.error('Mermaid render timeout - diagram took too long to render');
          setError('Diagram render timeout. The diagram may be too complex or contain syntax errors.');
          setIsLoading(false);
          if (diagramRef.current) {
            diagramRef.current.innerHTML = `<pre style="margin: 0; color: #d32f2f; font-size: 12px; padding: 10px; background: #fff8f8; border: 1px solid #ffcccc; border-radius: 4px;">Render timeout. Raw diagram code:\n\n${code}</pre>`;
          }
        }
      }, 10000);

      try {
        if (!diagramRef.current || !code?.trim()) {
          setIsLoading(false);
          clearTimeout(timeoutId);
          return;
        }

        // Check if this render attempt is still valid
        if (currentAttempt !== renderAttemptRef.current) {
          clearTimeout(timeoutId);
          return;
        }

        // Clear any existing content
        diagramRef.current.innerHTML = '';

        // Create unique ID using crypto for better uniqueness
        const uniqueId = Math.random().toString(36).substring(2, 11);
        const diagramId = `diagram-${index}-${uniqueId}`;

        // Render the mermaid diagram
        const { svg } = await mermaid.render(diagramId, code.trim());

        // Check if still mounted and this is the latest attempt
        if (!isMounted || currentAttempt !== renderAttemptRef.current) {
          clearTimeout(timeoutId);
          return;
        }

        diagramRef.current.innerHTML = svg;

        // Ensure the SVG scales properly
        const svgElement = diagramRef.current.querySelector('svg');
        if (svgElement) {
          svgElement.style.maxWidth = 'none';
          svgElement.style.height = 'auto';
        }

        clearTimeout(timeoutId);
        setError(null);
        setIsLoading(false);
      } catch (err) {
        clearTimeout(timeoutId);
        if (!isMounted || currentAttempt !== renderAttemptRef.current) return;

        console.error(`Error rendering Mermaid diagram:`, err);
        setError(err.message || 'Unknown error rendering diagram');
        setIsLoading(false);

        // Fallback to showing code if diagram fails to render
        if (diagramRef.current) {
          diagramRef.current.innerHTML = `<pre style="margin: 0; color: #d32f2f; font-size: 20px; padding: 10px; background: #fff8f8; border: 1px solid #ffcccc; border-radius: 4px;">Error: ${err.message || 'Failed to render diagram'}\n\nRaw code:\n${code}</pre>`;
        }
      }
    };

    // Reset state for new render
    setIsLoading(true);
    setError(null);

    // Small delay to ensure DOM is ready
    const delayId = setTimeout(renderDiagram, 150);

    return () => {
      isMounted = false;
      clearTimeout(timeoutId);
      clearTimeout(delayId);
    };
  }, [code, index]);
  
  // Base scale of 4 - zoomLevel adjusts from this base
  // zoomLevel 1 = scale 4, zoomLevel 0.5 = scale 2, zoomLevel 1.5 = scale 6
  const baseScale = 4;
  const effectiveZoom = baseScale * zoomLevel;

  return (
    <Box sx={{
      p: 3,
      backgroundColor: '#f9f9f9',
      border: '1px solid #e0e0e0',
      borderRadius: '8px',
      my: 2,
      position: 'relative',
      overflow: 'auto',
      maxHeight: '800px',
      minHeight: '500px',
      '&::-webkit-scrollbar': {
        width: '10px',
        height: '10px',
      },
      '&::-webkit-scrollbar-track': {
        backgroundColor: 'rgba(0, 0, 0, 0.05)',
        borderRadius: '5px',
      },
      '&::-webkit-scrollbar-thumb': {
        backgroundColor: 'rgba(0, 0, 0, 0.25)',
        borderRadius: '5px',
        '&:hover': {
          backgroundColor: 'rgba(0, 0, 0, 0.4)',
        },
      },
    }}>
      {isLoading && !error && (
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 1,
          zIndex: 1
        }}>
          <CircularProgress size={24} />
          <Typography variant="caption" color="text.secondary">
            Rendering diagram...
          </Typography>
        </Box>
      )}
      <Box sx={{
        display: 'flex',
        justifyContent: 'flex-start',
        alignItems: 'flex-start',
        minHeight: '400px',
        padding: '30px',
      }}>
        <div
          ref={diagramRef}
          className="mermaid-diagram"
          style={{
            transform: `scale(${effectiveZoom})`,
            transformOrigin: 'top left',
            transition: 'transform 0.3s ease',
            display: isLoading && !error ? 'none' : 'block',
         
          }}
        />
      </Box>
    </Box>
  );
};

// Memoize MermaidDiagram to prevent unnecessary re-renders
const MemoizedMermaidDiagram = memo(MermaidDiagram, (prevProps, nextProps) => {
  // Only re-render if code or index changes
  return prevProps.code === nextProps.code &&
         prevProps.index === nextProps.index &&
         prevProps.zoomLevel === nextProps.zoomLevel;
});

const MarkdownWithMermaid = ({ content, className, zoomLevel = 1 }) => {
  // Process Unicode escape sequences in the content - memoized to prevent re-processing
  const processedContent = useMemo(() => processUnicodeContent(content), [content]);

  // Track mermaid diagram index for unique IDs - use ref to maintain consistency across renders
  const mermaidIndexRef = useRef(0);
  // Reset index on content change
  useEffect(() => {
    mermaidIndexRef.current = 0;
  }, [content]);

  let mermaidIndex = 0;
  
  return (
    <Box className={className} sx={{ width: '100%', height: '100%', overflow: 'auto' }}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          code({ node, inline, className, children, ...props }) {
            const match = /language-(\w+)/.exec(className || '');
            const language = match ? match[1] : '';
            const codeString = String(children).replace(/\n$/, '');
            
            // Check if this is a mermaid code block
            if (!inline && language === 'mermaid') {
              const currentIndex = mermaidIndex++;
              return <MemoizedMermaidDiagram key={`mermaid-${currentIndex}`} code={codeString} index={currentIndex} zoomLevel={zoomLevel} />;
            }
            
            if (inline) {
              return (
                <Box 
                  component="code" 
                  sx={{ 
                    backgroundColor: '#f5f5f5', 
                    padding: '2px 4px', 
                    borderRadius: '4px',
                    fontFamily: 'monospace',
                    fontSize: '0.875rem'
                  }}
                  {...props}
                >
                  {children}
                </Box>
              );
            }
            
            return (
              <Box sx={{ 
                backgroundColor: '#f5f5f5', 
                padding: 2, 
                borderRadius: '4px',
                my: 2,
                overflow: 'auto',
                border: '1px solid #e0e0e0'
              }}>
                {language && (
                  <Box sx={{ 
                    backgroundColor: '#e0e0e0', 
                    px: 2, 
                    py: 0.5, 
                    mb: 1,
                    ml: -2,
                    mr: -2,
                    mt: -2,
                    borderBottom: '1px solid #d0d0d0',
                    borderTopLeftRadius: '4px',
                    borderTopRightRadius: '4px'
                  }}>
                    <Typography variant="caption" sx={{ 
                      fontWeight: 600, 
                      color: '#666',
                      textTransform: 'uppercase'
                    }}>
                      {language}
                    </Typography>
                  </Box>
                )}
                <Box 
                  component="pre" 
                  sx={{ 
                    fontFamily: 'monospace',
                    fontSize: '0.875rem',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    margin: 0
                  }}
                  {...props}
                >
                  {children}
                </Box>
              </Box>
            );
          },
          h1: ({ node, ...props }) => <Typography variant="h4" sx={{ mt: 3, mb: 2, fontWeight: 'bold' }} {...props} />,
          h2: ({ node, ...props }) => <Typography variant="h5" sx={{ mt: 3, mb: 2, fontWeight: 'bold' }} {...props} />,
          h3: ({ node, ...props }) => <Typography variant="h6" sx={{ mt: 2, mb: 1, fontWeight: 'bold' }} {...props} />,
          h4: ({ node, ...props }) => <Typography variant="subtitle1" sx={{ mt: 2, mb: 1, fontWeight: 'bold' }} {...props} />,
          h5: ({ node, ...props }) => <Typography variant="subtitle2" sx={{ mt: 1, mb: 1, fontWeight: 'bold' }} {...props} />,
          h6: ({ node, ...props }) => <Typography variant="body2" sx={{ mt: 1, mb: 1, fontWeight: 'bold' }} {...props} />,
          p: ({ node, ...props }) => <Typography variant="body1" sx={{ my: 1, lineHeight: 1.6 }} {...props} />,
          a: ({ node, ...props }) => <Link color="primary" underline="hover" {...props} />,
          ul: ({ node, ...props }) => <List sx={{ listStyleType: 'disc', pl: 4, my: 1, '& .MuiListItem-root': { display: 'list-item' } }} {...props} />,
          ol: ({ node, ...props }) => <List sx={{ listStyleType: 'decimal', pl: 4, my: 1, '& .MuiListItem-root': { display: 'list-item' } }} {...props} />,
          li: ({ node, ...props }) => <ListItem sx={{ py: 0.25 }} {...props} />,
          blockquote: ({ node, ...props }) => (
            <Box sx={{ 
              borderLeft: '4px solid #1976d2', 
              pl: 2, 
              py: 1, 
              my: 2,
              fontStyle: 'italic',
              color: 'text.secondary',
              backgroundColor: 'rgba(25, 118, 210, 0.05)'
            }} {...props} />
          ),
          img: ({ node, ...props }) => (
            <Box 
              component="img" 
              sx={{ 
                maxWidth: '100%', 
                height: 'auto',
                borderRadius: '4px',
                my: 2,
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
              }} 
              {...props} 
              alt={props.alt || ""} 
            />
          ),
          table: ({ node, ...props }) => (
            <Box sx={{ overflowX: 'auto', my: 2 }}>
              <Box 
                component="table" 
                sx={{ 
                  width: '100%',
                  borderCollapse: 'collapse',
                  fontSize: '0.875rem',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                  borderRadius: '4px',
                  overflow: 'hidden'
                }} 
                {...props} 
              />
            </Box>
          ),
          th: ({ node, ...props }) => (
            <Box
              component="th"
              sx={{
                border: '1px solid #ddd',
                padding: '8px 12px',
                backgroundColor: '#f5f5f5',
                textAlign: 'left',
                fontWeight: 'bold'
              }}
              {...props}
            />
          ),
          td: ({ node, ...props }) => (
            <Box
              component="td"
              sx={{
                border: '1px solid #ddd',
                padding: '8px 12px'
              }}
              {...props}
            />
          ),
          tr: ({ node, ...props }) => (
            <Box
              component="tr"
              sx={{
                '&:nth-of-type(even)': {
                  backgroundColor: '#f9f9f9'
                }
              }}
              {...props}
            />
          ),
          hr: ({ node, ...props }) => (
            <Box
              component="hr"
              sx={{
                border: 'none',
                height: '1px',
                backgroundColor: '#ddd',
                my: 3
              }}
              {...props}
            />
          ),
          strong: ({ node, ...props }) => (
            <Box component="strong" sx={{ fontWeight: 'bold', color: '#1976d2' }} {...props} />
          ),
          em: ({ node, ...props }) => (
            <Box component="em" sx={{ fontStyle: 'italic' }} {...props} />
          ),
          div: ({ node, className, ...props }) => {
            if (className === 'numbered-item') {
              return (
                <Box sx={{ display: 'flex', mb: 1 }} {...props} />
              );
            }
            return <Box {...props} />;
          }
        }}
      >
        {processedContent}
      </ReactMarkdown>
    </Box>
  );
};

// Memoize the entire component to prevent unnecessary re-renders from parent
export default memo(MarkdownWithMermaid, (prevProps, nextProps) => {
  return prevProps.content === nextProps.content &&
         prevProps.className === nextProps.className &&
         prevProps.zoomLevel === nextProps.zoomLevel;
});